// ADR 30.9.26h §5 — the one storage idiom for the learner's settings.
//
// Every hook in this folder is a `useSyncExternalStore` over exactly one
// `localStorage` key, spelled as the phones spell it (9.9.26f). Three rules,
// each the answer to a way this goes wrong:
//
// - **The snapshot is cached by its raw string.** `getSnapshot` re-reads the
//   string on every call and parses only when it moved; otherwise it hands back
//   the previous object. A fresh object per call makes `useSyncExternalStore`
//   re-render forever. A corrupt string parses to the default once, and that
//   default is cached like any other value.
// - **Every read and write is `try`/`catch`** (`flashcards/settings.ts`'s
//   shape): a storage-denied browser reads the default, and a failed write does
//   nothing rather than breaking the page that made it.
// - **Same-tab writes notify directly; other tabs arrive as `storage` events**,
//   so a second tab follows a change without a reload.

import { useCallback, useSyncExternalStore } from 'react'

export interface PrefStore<T> {
  readonly key: string
  read(): T
  write(next: T): void
  subscribe(onChange: () => void): () => void
}

function rawOf(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * A store over `key`: `parse` must never throw, and maps `null` to the default.
 * A `serialize` that returns `null` removes the key (ADR 1.10.26o §1), so a
 * phone that reads it with `getString(KEY, null)` sees None, not `''`.
 */
export function prefStore<T>(
  key: string,
  parse: (raw: string | null) => T,
  serialize: (value: T) => string | null,
): PrefStore<T> {
  const listeners = new Set<() => void>()
  let cached: { raw: string | null; value: T } | null = null

  function read(): T {
    const raw = rawOf(key)
    if (cached === null || cached.raw !== raw) cached = { raw, value: parse(raw) }
    return cached.value
  }

  function write(next: T): void {
    try {
      const raw = serialize(next)
      if (raw === null) window.localStorage.removeItem(key)
      else window.localStorage.setItem(key, raw)
    } catch {
      // A browser that refuses storage keeps the default; a setting that
      // cannot persist must not be the thing that breaks the page.
    }
    listeners.forEach((listener) => listener())
  }

  function subscribe(onChange: () => void): () => void {
    const onStorage = (event: StorageEvent) => {
      if (event.key === key || event.key === null) onChange()
    }
    listeners.add(onChange)
    window.addEventListener('storage', onStorage)
    return () => {
      listeners.delete(onChange)
      window.removeEventListener('storage', onStorage)
    }
  }

  return { key, read, write, subscribe }
}

/** A `"true"`/`"false"` key: Android's typed boolean, in its string spelling (§1). */
export function booleanPref(key: string, fallback: boolean): PrefStore<boolean> {
  return prefStore(
    key,
    (raw) => (raw === 'true' ? true : raw === 'false' ? false : fallback),
    String,
  )
}

/**
 * `booleanPref` plus the page's own last write while storage refuses it: a
 * refused write still notifies, but the re-read would answer the default, so
 * without this the toggle could never open the drawer in such a browser (ADR 9.10.26j §7;
 * exported for the builder's files drawer, ADR 16.10.26m §3). Generic since
 * ADR 31.10.26s §2, for the canvas's saved views: with an array `T` the
 * `=== next` check never clears the memory (`parse` builds a new array), which
 * is harmless, since the memory answers only while `getItem` still throws.
 */
export function pageRemembered<T>(inner: PrefStore<T>): PrefStore<T> {
  let refused: { value: T } | null = null
  // The memory answers only while storage itself still refuses, so a browser that recovers reads the key again.
  const refusing = () => {
    try {
      window.localStorage.getItem(inner.key)
      return false
    } catch {
      return true
    }
  }
  return {
    key: inner.key,
    read: () => (refused !== null && refusing() ? refused.value : inner.read()),
    write(next) {
      // Set first: `inner.write` notifies before it returns, and the listeners re-read.
      refused = { value: next }
      inner.write(next)
      if (inner.read() === next) refused = null
    },
    subscribe: inner.subscribe,
  }
}

/** Subscribe a component to one store: the value and its writer. */
export function usePref<T>(store: PrefStore<T>): [T, (next: T) => void] {
  const value = useSyncExternalStore(store.subscribe, store.read, store.read)
  const write = useCallback((next: T) => store.write(next), [store])
  return [value, write]
}
